import { useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, ScrollText } from "lucide-react";
import { cn } from "@/lib/utils";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { useAssessment } from "@/context/AssessmentContext";
import sbcLogo from "@/assets/sbc-logo-light-canvas-1200x400.png";

const NAV = [
  { to: "/", label: "Overview", requiresAssessment: false },
  { to: "/intake", label: "Intake", requiresAssessment: false },
  { to: "/results", label: "Snapshot", requiresAssessment: true },
  { to: "/report", label: "Report", requiresAssessment: true }
];

function isActive(pathname, to) {
  if (to === "/") return pathname === "/";
  return pathname.startsWith(to);
}

function NavItem({ item, active, locked, onClick, variant = "desktop" }) {
  const base =
    variant === "desktop"
      ? "font-mono-label rounded-sm px-3 py-2 text-[11px] uppercase tracking-[0.18em] transition-colors"
      : "font-mono-label flex w-full items-center justify-between rounded-sm border border-transparent px-3 py-3 text-[12px] uppercase tracking-[0.18em] transition-colors";

  if (locked) {
    return (
      <span
        className={cn(
          base,
          "cursor-not-allowed text-muted-foreground/50",
          variant === "mobile" && "border-border/60 bg-muted/30"
        )}
        title="Complete the readiness intake to unlock this section"
        aria-disabled="true"
      >
        {item.label}
        <span className="ml-2 font-mono-label text-[9px] uppercase tracking-[0.18em] text-muted-foreground/70">
          Locked
        </span>
      </span>
    );
  }

  return (
    <NavLink
      to={item.to}
      onClick={onClick}
      className={cn(
        base,
        active
          ? "bg-secondary text-foreground"
          : "text-muted-foreground hover:text-foreground hover:bg-secondary/60"
      )}
    >
      <span>{item.label}</span>
      {variant === "mobile" && active && (
        <span className="font-mono-label text-[10px] uppercase tracking-[0.18em] text-accent">
          Now viewing
        </span>
      )}
    </NavLink>
  );
}

export function AppHeader() {
  const location = useLocation();
  const { completedAt, result } = useAssessment();
  const assessmentReady = Boolean(completedAt && result);
  const [mobileOpen, setMobileOpen] = useState(false);

  return (
    <header className="report-no-print sticky top-0 z-30 border-b border-border/80 bg-background/85 backdrop-blur-md">
      <div className="container flex h-16 items-center justify-between gap-4">
        <Link to="/" className="group flex items-center gap-3" data-testid="app-header-logo-link">
          <img
            src={sbcLogo}
            alt="Spiral Beyond Compliance"
            className="h-9 w-auto object-contain sm:h-10"
            data-testid="app-header-logo"
          />
          <span className="flex flex-col leading-tight">
            <span className="font-display text-sm font-medium text-foreground sm:text-[15px]">
              Spiral Beyond Compliance
            </span>
            <span className="text-[11px] leading-snug text-muted-foreground">
              People before process.
            </span>
          </span>
        </Link>

        <nav className="hidden items-center gap-1 lg:flex" aria-label="primary">
          {NAV.map((item) => {
            const locked = item.requiresAssessment && !assessmentReady;
            const active = !locked && isActive(location.pathname, item.to);
            return (
              <NavItem
                key={item.to}
                item={item}
                active={active}
                locked={locked}
                variant="desktop"
              />
            );
          })}
        </nav>

        <div className="flex items-center gap-2">
          <span className="hidden items-center gap-2 rounded-sm border border-border bg-card px-2.5 py-1.5 text-[11px] text-muted-foreground lg:inline-flex">
            <ScrollText className="h-3.5 w-3.5" />
            <span className="font-mono-label uppercase tracking-[0.18em]">Guidance only</span>
          </span>
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button
                type="button"
                variant="outline"
                size="icon"
                className="rounded-sm border-border bg-card lg:hidden"
                aria-label="Open navigation menu"
                data-testid="mobile-nav-trigger"
              >
                <Menu className="h-4 w-4" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72 border-l border-border bg-background">
              <SheetHeader className="space-y-0 text-left">
                <SheetTitle className="font-display text-base font-medium text-foreground">
                  NDIS 0138 Readiness Navigation
                </SheetTitle>
                <SheetDescription className="text-xs text-muted-foreground">
                  Move between the four sections of the readiness tool.
                </SheetDescription>
              </SheetHeader>
              <div className="mt-6 flex flex-col gap-1">
                <p className="eyebrow mb-2">Sections</p>
                {NAV.map((item) => {
                  const locked = item.requiresAssessment && !assessmentReady;
                  const active = !locked && isActive(location.pathname, item.to);
                  return (
                    <NavItem
                      key={item.to}
                      item={item}
                      active={active}
                      locked={locked}
                      variant="mobile"
                      onClick={() => setMobileOpen(false)}
                    />
                  );
                })}
                <div className="mt-4 rounded-sm border border-border bg-card/60 p-3 text-xs leading-relaxed text-muted-foreground">
                  {assessmentReady
                    ? "Snapshot and Report are ready. Use Start new assessment to clear local state."
                    : "Snapshot and Report unlock automatically once the readiness intake is complete."}
                </div>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </header>
  );
}
